import { useMemo, useState } from "react";
import moment from "moment-timezone";
import "moment/locale/fr";
import "moment/locale/es";
import "moment/locale/sv";
import {
  hasOpeningHours,
  calculateHistoryDays,
  extractReliablePrediction,
} from "../../shared/utils/utils";
import { utcToLocalOpening } from "../address/OpeningHoursEditor";
import { useTranslation } from "react-i18next";
import { formattedPredictionDateTime } from "../../components/formatted/FormattedDateAndTime";
import {
  CALENDAR_WINDOW_DAYS,
  MIN_HISTORY_DAYS_FOR_PREDICTION,
  ONE_DAY_MS,
} from "../../constants/cache";

const EIGHT_DAYS_MS = CALENDAR_WINDOW_DAYS * ONE_DAY_MS;
const GRACE_MS = 0;

const PALETTE = [
  "#0ea5e9",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#06b6d4",
  "#eab308",
  "#14b8a6",
  "#f97316",
  "#6366f1",
];

function normalizeSiteKey(s = "") {
  const raw = String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
  const prefixes = [
    /^dechetterie\s+(de|du|des)\s+/,
    /^chantier\s+(de|du|des)\s+/,
    /^(site|usine|agence)\s+(de|du|des)\s+/,
  ];
  let out = raw.trim();
  for (const rx of prefixes) out = out.replace(rx, "");
  out = out
    .replace(/\b(de|du|des|la|le|les|d')\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return out || raw || "default";
}

function djb2(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h << 5) + h + str.charCodeAt(i);
  return Math.abs(h);
}

const colorForSite = (siteName = "", fallback = "") => {
  const key = normalizeSiteKey(siteName) || String(fallback || "");
  const idx = djb2(key) % PALETTE.length;
  return PALETTE[idx];
};

function darken(hex, amt = 0.2) {
  try {
    const { r, g, b } = hexToRgb(hex);
    const f = 1 - Math.max(0, Math.min(1, amt));
    return rgbToHex(Math.floor(r * f), Math.floor(g * f), Math.floor(b * f));
  } catch {
    return hex;
  }
}

function readableTextColor(hex) {
  try {
    const { r, g, b } = hexToRgb(hex);
    const L = 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
    return L > 0.6 ? "#111827" : "#ffffff";
  } catch {
    return "#111827";
  }
}

function srgb(c) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const bigint = parseInt(h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function rgbToHex(r, g, b) {
  const toHex = (x) => x.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const FillingCalendar = ({ containers }) => {
  const { t, i18n } = useTranslation();
  const [showInfoSection, setShowInfoSection] = useState(true);
  const [showInsufficientHistoryList, setShowInsufficientHistoryList] =
    useState(false);
  const timezone = useMemo(() => {
    if (!Array.isArray(containers) || containers.length === 0) {
      return "Europe/Paris";
    }
    const tz =
      containers.find((c) => c?.addressJoin?.timezone || c?.addressTimezone)
        ?.addressJoin?.timezone ||
      containers.find((c) => c?.addressTimezone)?.addressTimezone;
    return tz || "Europe/Paris";
  }, [containers]);

  const monthWeekInfo = useMemo(() => {
    const now = new Date();
    const windowEnd = new Date(now.getTime() + EIGHT_DAYS_MS);
    const months = new Set();
    const weeks = new Set();

    const locale = i18n.language || "fr";
    const nowMoment = moment(now).tz(timezone).locale(locale);
    months.add(nowMoment.format("MMMM YYYY"));
    weeks.add(nowMoment.week());

    const endMoment = moment(windowEnd).tz(timezone).locale(locale);
    months.add(endMoment.format("MMMM YYYY"));
    weeks.add(endMoment.week());

    if (Array.isArray(containers) && containers.length > 0) {
      for (const c of containers) {
        const durationData = c?.durationFullFillingMs?.[0];
        if (!durationData?.predictionDatetime) continue;

        const containerTimezone =
          durationData.timezone ||
          c?.addressJoin?.timezone ||
          c?.addressTimezone ||
          timezone;
        const locale = i18n.language || "fr";
        const fillDateMoment = moment(durationData.predictionDatetime)
          .tz(containerTimezone)
          .locale(locale);
        if (!fillDateMoment.isValid()) continue;

        const fillDate = fillDateMoment.toDate();
        if (fillDate <= now || fillDate > windowEnd) continue;

        const monthKey = fillDateMoment.format("MMMM YYYY");
        months.add(monthKey);

        const weekNum = fillDateMoment.week();
        weeks.add(weekNum);
      }
    }

    return {
      months: Array.from(months).sort(),
      weeks: Array.from(weeks).sort((a, b) => a - b),
    };
  }, [containers, timezone, i18n.language]);

  const { hasInsufficientHistory, containersWithInsufficientHistory } =
    useMemo(() => {
      if (!Array.isArray(containers) || containers.length === 0) {
        return {
          hasInsufficientHistory: false,
          containersWithInsufficientHistory: [],
        };
      }
      const now = new Date();
      const insufficientContainers = [];

      for (const c of containers) {
        const fromDate = c?.fromDate;
        const reliablePrediction = extractReliablePrediction(c);

        if (
          reliablePrediction === true &&
          hasOpeningHours(c?.addressJoin?.opening || c?.addressOpening) &&
          fromDate
        ) {
          // Calculate history days with UTC normalization to avoid timezone issues
          const historyDays = calculateHistoryDays(fromDate, now);

          // If available history is less than the required threshold
          if (
            historyDays !== null &&
            historyDays < MIN_HISTORY_DAYS_FOR_PREDICTION
          ) {
            insufficientContainers.push({
              id: c?._id,
              name: c?.wasteName || c?.contentCategoryJoin?.label || t("Flux"),
              site: c?.siteName || c?.addressName || t("Unknown"),
              positioning: c?.positioning || "",
            });
          }
        }
      }
      return {
        hasInsufficientHistory: insufficientContainers.length > 0,
        containersWithInsufficientHistory: insufficientContainers,
      };
    }, [containers, t]);

  const { urgentContainers, events } = useMemo(() => {
    const result = { urgentContainers: [], events: [] };
    if (!Array.isArray(containers) || containers.length === 0) return result;
    const now = new Date();
    const windowEnd = new Date(now.getTime() + EIGHT_DAYS_MS);

    for (const c of containers) {
      const durationData = c?.durationFullFillingMs?.[0];
      if (!durationData) continue;

      if (durationData.duration === -1) {
        const site = c?.siteName ?? c?.addressName ?? "Site inconnu";
        const title = c?.wasteName ?? c?.contentCategoryJoin?.label ?? "Flux";
        const color = colorForSite(site, c?._id ?? c?.addressName ?? "");
        const fillDate = new Date();

        const containerAlert = c?.fillingAlert;
        let recommendedAction = null;
        if (containerAlert) {
          if (containerAlert.rotationAlert) {
            recommendedAction = "rotation";
          } else if (
            containerAlert.packmatAlert ||
            containerAlert.packmatPossibility
          ) {
            recommendedAction = "compaction";
          }
        }

        result.urgentContainers.push({
          id: String(c?._id ?? `${title}-${fillDate.getTime()}`),
          title,
          site,
          positioning: c?.positioning || "",
          fillDate,
          color,
          recommendedAction,
        });
        continue;
      }

      if (!durationData.predictionDatetime) continue;

      const containerTimezone =
        durationData.timezone ||
        c?.addressJoin?.timezone ||
        c?.addressTimezone ||
        timezone;
      const fillDateMoment = moment(durationData.predictionDatetime).tz(
        containerTimezone,
      );
      if (!fillDateMoment.isValid()) continue;

      const site = c?.siteName ?? c?.addressName ?? "Site inconnu";
      const title = c?.wasteName ?? c?.contentCategoryJoin?.label ?? "Flux";
      const color = colorForSite(site, c?._id ?? c?.addressName ?? "");

      const fillDateLocal = fillDateMoment.toDate();

      if (fillDateLocal <= now) {
        const containerAlert = c?.fillingAlert;
        let recommendedAction = null;
        if (containerAlert) {
          if (containerAlert.rotationAlert) {
            recommendedAction = "rotation";
          } else if (
            containerAlert.packmatAlert ||
            containerAlert.packmatPossibility
          ) {
            recommendedAction = "compaction";
          }
        }

        result.urgentContainers.push({
          id: String(c?._id ?? `${title}-${fillDateLocal.getTime()}`),
          title,
          site,
          positioning: c?.positioning || "",
          fillDate: fillDateLocal,
          color,
          recommendedAction,
        });
        continue;
      }

      if (fillDateLocal > windowEnd) continue;
      if (fillDateLocal < new Date(now.getTime() - GRACE_MS)) continue;

      const daysLeft =
        (fillDateLocal.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
      const bg = color;
      const border = daysLeft <= 2 ? darken(color, 0.25) : color;

      // Store the prediction moment for centering
      const predictionMoment = fillDateMoment.clone();

      const blockDurationMs = 60 * 60 * 1000;
      let eventStartMoment = predictionMoment
        .clone()
        .subtract(blockDurationMs / 2, "milliseconds");
      let eventEndMoment = predictionMoment
        .clone()
        .add(blockDurationMs / 2, "milliseconds");

      const openingUtc = c?.addressJoin?.opening || c?.addressOpening;
      if (openingUtc && hasOpeningHours(openingUtc)) {
        const containerTz =
          c?.addressJoin?.timezone || c?.addressTimezone || containerTimezone;
        const openingLocal = utcToLocalOpening(openingUtc, containerTz);
        const dayOfWeek = fillDateMoment.day();
        const dayRanges = openingLocal[dayOfWeek]?.ranges || [];

        if (dayRanges.length > 0) {
          const dayStart = fillDateMoment.clone().startOf("day");

          let bestSlot = null;
          let minDistance = Infinity;

          for (const range of dayRanges) {
            if (!range?.start || !range?.end) continue;

            const [startH, startM] = range.start.split(":").map(Number);
            const [endH, endM] = range.end.split(":").map(Number);

            const slotStart = dayStart
              .clone()
              .hour(startH)
              .minute(startM || 0)
              .second(0);
            const slotEnd = dayStart
              .clone()
              .hour(endH)
              .minute(endM || 0)
              .second(0);

            if (fillDateMoment.isBetween(slotStart, slotEnd, null, "[]")) {
              bestSlot = { start: slotStart, end: slotEnd };
              break;
            }

            const distToStart = Math.abs(fillDateMoment.diff(slotStart));
            const distToEnd = Math.abs(fillDateMoment.diff(slotEnd));
            const minDist = Math.min(distToStart, distToEnd);

            if (minDist < minDistance) {
              minDistance = minDist;
              bestSlot = { start: slotStart, end: slotEnd };
            }
          }

          if (bestSlot) {
            if (eventStartMoment.isBefore(bestSlot.start)) {
              eventStartMoment = bestSlot.start.clone();
            }
            if (eventEndMoment.isAfter(bestSlot.end)) {
              eventEndMoment = bestSlot.end.clone();
            }

            if (eventEndMoment.diff(eventStartMoment) < blockDurationMs / 2) {
              const centerTime = fillDateMoment.valueOf();
              const halfDuration = Math.min(
                blockDurationMs / 2,
                centerTime - bestSlot.start.valueOf(),
                bestSlot.end.valueOf() - centerTime,
              );
              eventStartMoment = moment.tz(
                centerTime - halfDuration,
                containerTimezone,
              );
              eventEndMoment = moment.tz(
                centerTime + halfDuration,
                containerTimezone,
              );
            }
          } else {
            // Fallback: use 1 hour block centered on prediction
            eventStartMoment = predictionMoment.clone().subtract(30, "minutes");
            eventEndMoment = predictionMoment.clone().add(30, "minutes");
          }
        }
      }

      const containerAlert = c?.fillingAlert;
      let recommendedAction = null;
      if (containerAlert) {
        if (containerAlert.rotationAlert) {
          recommendedAction = "rotation";
        } else if (
          containerAlert.packmatAlert ||
          containerAlert.packmatPossibility
        ) {
          recommendedAction = "compaction";
        }
      }

      result.events.push({
        id: String(c?._id ?? `${title}-${fillDateMoment.valueOf()}`),
        title,
        start: eventStartMoment,
        end: eventEndMoment,
        backgroundColor: bg,
        borderColor: border,
        textColor: readableTextColor(bg),
        extendedProps: {
          site,
          address: c?.addressName ?? "Adresse inconnue",
          positioning: c?.positioning || "",
          daysLeft,
          predictionDatetime: durationData.predictionDatetime,
          predictionMoment: predictionMoment,
          containerTimezone,
          recommendedAction,
        },
      });
    }

    result.urgentContainers.sort((a, b) => a.fillDate - b.fillDate);
    return result;
  }, [containers, timezone]);

  const days = useMemo(() => {
    const now = new Date();
    const days = [];
    for (let i = 0; i < 8; i++) {
      const date = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
      days.push(date);
    }
    return days;
  }, []);

  const hours = useMemo(() => {
    const hours = [];
    for (let h = 6; h <= 20; h++) {
      hours.push(h);
    }
    return hours;
  }, []);

  const getEventsForDay = (dayMoment) => {
    const dayStart = dayMoment.clone().startOf("day");
    const dayEnd = dayMoment.clone().endOf("day");

    return events.filter((event) => {
      const eventStart = moment(event.start).tz(timezone);
      const eventEnd = moment(event.end).tz(timezone);

      return eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart);
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const urgentContainersToEvacuate = urgentContainers.filter(
    (u) => u.recommendedAction === "rotation" || !u.recommendedAction,
  );
  const urgentContainersToCompact = urgentContainers.filter(
    (u) => u.recommendedAction === "compaction",
  );

  return (
    <div className="fc-body-flat">
      {(urgentContainersToEvacuate.length > 0 ||
        urgentContainersToCompact.length > 0) && (
        <div className="urgent-banner">
          <div className="urgent-title-wrapper">
            <svg
              width="21"
              height="18"
              viewBox="0 0 21 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="urgent-icon"
            >
              <path
                d="M10.0833 3.6575L16.9858 15.5833H3.18083L10.0833 3.6575ZM10.0833 0L0 17.4167H20.1667L10.0833 0Z"
                fill="#EEAC43"
              />
              <path
                d="M11 12.8333H9.16667V14.6667H11V12.8333Z"
                fill="#EEAC43"
              />
              <path
                d="M11 7.33333H9.16667V11.9167H11V7.33333Z"
                fill="#EEAC43"
              />
            </svg>
            <div className="urgent-title">
              {t("Threshold limit reached on some containers")}
            </div>
          </div>

          {urgentContainersToEvacuate.length > 0 && (
            <>
              <div className="urgent-subtitle">
                {t("Containers to evacuate")}
              </div>
              <div className="urgent-list">
                {urgentContainersToEvacuate.map((u) => (
                  <div key={u.id} className="urgent-item">
                    <span className="urgent-item-text">
                      {u.title} — {u.site}
                      {u.positioning
                        ? ` — ${u.positioning.charAt(0).toUpperCase() + u.positioning.slice(1)}`
                        : ""}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {urgentContainersToCompact.length > 0 && (
            <>
              <div
                className="urgent-subtitle"
                style={{
                  marginTop:
                    urgentContainersToEvacuate.length > 0 ? "12px" : "4px",
                }}
              >
                {t("Containers to compact")}
              </div>
              <div className="urgent-list">
                {urgentContainersToCompact.map((u) => (
                  <div key={u.id} className="urgent-item">
                    <span className="urgent-item-text">
                      {u.title} — {u.site}
                      {u.positioning
                        ? ` — ${u.positioning.charAt(0).toUpperCase() + u.positioning.slice(1)}`
                        : ""}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {showInfoSection && (
        <div className="info-banner">
          <button
            onClick={() => setShowInfoSection(false)}
            className="info-close-button"
            title={t("Close")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="info-content">
            <div className="info-svg-wrapper">
              <svg
                width="102"
                height="66"
                viewBox="0 0 102 66"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M89.0758 66.0002H81.751V65.695H89.0758C89.9372 65.6773 90.7787 65.4319 91.5146 64.9837C92.2506 64.5356 92.8549 63.9007 93.2662 63.1436L93.5347 63.2901C93.0971 64.0954 92.454 64.7704 91.6707 65.2465C90.8875 65.7225 89.9922 65.9826 89.0758 66.0002Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M80.3014 65.6948H77.3013V66H80.3014V65.6948Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M89.0759 64.932H3.37592C2.90605 64.9412 2.43961 64.8502 2.00761 64.6652C1.57562 64.4801 1.18795 64.2053 0.870392 63.8588C0.552831 63.5124 0.312645 63.1024 0.165801 62.6559C0.0189579 62.2095 -0.0311801 61.7369 0.0187202 61.2696L4.6486 8.16491C4.75212 7.17664 5.21191 6.25986 5.94207 5.58586C6.67223 4.91187 7.62281 4.52677 8.61619 4.50252H94.3162C94.7859 4.49386 95.2521 4.58515 95.6839 4.77036C96.1157 4.95556 96.5031 5.23043 96.8206 5.57675C97.138 5.92307 97.3783 6.33292 97.5253 6.77913C97.6723 7.22534 97.7228 7.69771 97.6734 8.16491L93.071 61.2696C92.9669 62.2625 92.5031 63.183 91.7672 63.8576C91.0313 64.5322 90.0741 64.9144 89.0759 64.932Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M87.2906 63.8181H3.72696C3.32687 63.824 2.9301 63.7447 2.56301 63.5854C2.19592 63.4262 1.86693 63.1907 1.59788 62.8945C1.32883 62.5983 1.1259 62.2482 1.00255 61.8676C0.879212 61.4869 0.838289 61.0844 0.882504 60.6867L5.10036 12.3095C5.12557 12.0671 5.23838 11.8421 5.41763 11.6769C5.59688 11.5117 5.83024 11.4176 6.07395 11.4122H94.0843C94.1992 11.4102 94.3132 11.4327 94.4186 11.4782C94.5241 11.5237 94.6187 11.5911 94.6961 11.676C94.7735 11.7609 94.8319 11.8613 94.8674 11.9706C94.903 12.0798 94.9149 12.1953 94.9023 12.3095L90.6814 60.6867C90.5959 61.5326 90.2039 62.318 89.5793 62.8948C88.9547 63.4716 88.1406 63.8 87.2906 63.8181Z"
                  fill="white"
                />
                <path
                  d="M14.3783 9.00398C13.8018 9.01382 13.2298 8.90091 12.7003 8.67276C12.1708 8.44461 11.6958 8.10644 11.307 7.68069C10.9182 7.25494 10.6244 6.75135 10.4452 6.20336C10.2659 5.65537 10.2052 5.07552 10.2672 4.50229C10.3927 3.2857 10.9581 2.15683 11.8574 1.32785C12.7566 0.49887 13.9276 0.026881 15.1504 0.000597879C15.7268 -0.00929149 16.2987 0.103603 16.828 0.331781C17.3574 0.559959 17.8321 0.898192 18.2207 1.324C18.6093 1.74981 18.9028 2.25345 19.0817 2.80144C19.2606 3.34943 19.3208 3.92923 19.2584 4.50229H17.4272C17.4641 4.16198 17.4281 3.81772 17.3216 3.4924C17.2151 3.16708 17.0406 2.86817 16.8096 2.61554C16.5786 2.36291 16.2965 2.16236 15.982 2.02722C15.6675 1.89209 15.3278 1.82547 14.9856 1.8318C14.2587 1.84512 13.5618 2.12412 13.0265 2.61612C12.4912 3.10812 12.1545 3.77906 12.0801 4.50229C12.0432 4.8426 12.0792 5.18686 12.1857 5.51218C12.2922 5.8375 12.4667 6.13641 12.6977 6.38904C12.9287 6.64167 13.2108 6.84222 13.5253 6.97736C13.8398 7.11249 14.1795 7.17911 14.5217 7.17279L14.3783 9.00398Z"
                  fill="#263238"
                />
                <path
                  d="M32.492 9.00398C31.9145 9.01557 31.3412 8.90396 30.8102 8.67659C30.2792 8.44921 29.8028 8.11129 29.4127 7.68534C29.0225 7.25939 28.7276 6.75519 28.5476 6.20633C28.3676 5.65747 28.3067 5.07656 28.3688 4.50229C28.4942 3.2857 29.0597 2.15683 29.959 1.32785C30.8582 0.49887 32.0292 0.026881 33.252 0.000597879C33.8284 -0.00929149 34.4002 0.103603 34.9296 0.331781C35.459 0.559959 35.9337 0.898192 36.3223 1.324C36.7109 1.74981 37.0044 2.25345 37.1833 2.80144C37.3622 3.34943 37.4224 3.92923 37.36 4.50229H35.5288C35.5657 4.16198 35.5297 3.81772 35.4232 3.4924C35.3167 3.16708 35.1422 2.86817 34.9112 2.61554C34.6802 2.36291 34.3981 2.16236 34.0836 2.02722C33.7691 1.89209 33.4294 1.82547 33.0872 1.8318C32.3603 1.84512 31.6634 2.12412 31.1281 2.61612C30.5928 3.10812 30.2561 3.77906 30.1817 4.50229C30.1453 4.8425 30.1816 5.18656 30.2883 5.51166C30.395 5.83676 30.5696 6.13547 30.8004 6.38799C31.0313 6.64051 31.3132 6.84106 31.6275 6.97637C31.9417 7.11167 32.2812 7.17862 32.6233 7.17279L32.492 9.00398Z"
                  fill="#263238"
                />
                <path
                  d="M50.6052 9.00398C50.0289 9.01338 49.4573 8.90015 48.9282 8.67181C48.399 8.44346 47.9245 8.10522 47.536 7.67952C47.1475 7.25382 46.854 6.75039 46.6749 6.20262C46.4958 5.65485 46.4352 5.07526 46.4972 4.50229C46.6214 3.2852 47.1865 2.15557 48.086 1.32635C48.9855 0.497117 50.1572 0.025573 51.3804 0.000597879C51.9568 -0.00929149 52.5286 0.103603 53.058 0.331781C53.5874 0.559959 54.0621 0.898192 54.4507 1.324C54.8393 1.74981 55.1328 2.25345 55.3117 2.80144C55.4906 3.34943 55.5508 3.92923 55.4884 4.50229H53.6572C53.6946 4.16189 53.6589 3.81742 53.5526 3.49188C53.4463 3.16634 53.2718 2.86722 53.0407 2.61448C52.8097 2.36174 52.5273 2.16119 52.2126 2.02622C51.8979 1.89126 51.558 1.82497 51.2156 1.8318C50.489 1.8452 49.7925 2.12431 49.2577 2.61636C48.7229 3.10842 48.3869 3.77933 48.3131 4.50229C48.2762 4.8426 48.3122 5.18686 48.4187 5.51218C48.5252 5.8375 48.6997 6.13641 48.9307 6.38904C49.1617 6.64167 49.4438 6.84222 49.7583 6.97736C50.0728 7.11249 50.4125 7.17911 50.7547 7.17279L50.6052 9.00398Z"
                  fill="#263238"
                />
                <path
                  d="M68.7189 9.00398C68.1427 9.01338 67.5711 8.90014 67.0419 8.6718C66.5128 8.44346 66.0382 8.10522 65.6498 7.67952C65.2613 7.25382 64.9678 6.75039 64.7887 6.20261C64.6096 5.65484 64.549 5.07525 64.611 4.50229C64.7358 3.28545 65.3011 2.15619 66.2004 1.32709C67.0998 0.497988 68.2712 0.0262213 69.4941 0.000591448C70.0706 -0.00924279 70.6426 0.103661 71.1721 0.331812C71.7016 0.559962 72.1766 0.898137 72.5654 1.32389C72.9542 1.74964 73.248 2.25322 73.4272 2.80121C73.6065 3.3492 73.6672 3.92906 73.6052 4.50229H71.774C71.8109 4.16198 71.7749 3.81771 71.6684 3.49239C71.5619 3.16708 71.3874 2.86816 71.1564 2.61553C70.9254 2.3629 70.6433 2.16235 70.3288 2.02722C70.0143 1.89208 69.6746 1.82546 69.3324 1.83179C68.6058 1.8452 67.9093 2.1243 67.3745 2.61636C66.8397 3.10841 66.5037 3.77932 66.4299 4.50229C66.3931 4.84259 66.429 5.18686 66.5355 5.51217C66.642 5.83749 66.8166 6.13641 67.0475 6.38904C67.2785 6.64166 67.5606 6.84222 67.8751 6.97735C68.1896 7.11249 68.5293 7.17911 68.8715 7.17278L68.7189 9.00398Z"
                  fill="#263238"
                />
                <path
                  d="M86.8327 9.00398C86.2565 9.01338 85.6848 8.90014 85.1557 8.6718C84.6266 8.44346 84.152 8.10522 83.7635 7.67952C83.3751 7.25382 83.0816 6.75039 82.9025 6.20261C82.7234 5.65484 82.6628 5.07525 82.7247 4.50229C82.8495 3.28545 83.4148 2.15619 84.3142 1.32709C85.2136 0.497988 86.385 0.0262213 87.6079 0.000591448C88.1844 -0.00924279 88.7564 0.103661 89.2859 0.331812C89.8154 0.559962 90.2903 0.898137 90.6791 1.32389C91.0679 1.74964 91.3617 2.25322 91.541 2.80121C91.7203 3.3492 91.7809 3.92906 91.719 4.50229H89.8878C89.9246 4.16198 89.8887 3.81771 89.7822 3.49239C89.6757 3.16708 89.5011 2.86816 89.2702 2.61553C89.0392 2.3629 88.7571 2.16235 88.4426 2.02722C88.1281 1.89208 87.7884 1.82546 87.4462 1.83179C86.7198 1.84586 86.0236 2.12518 85.489 2.61711C84.9543 3.10904 84.6181 3.77957 84.5437 4.50229C84.5068 4.84259 84.5428 5.18686 84.6493 5.51217C84.7558 5.83749 84.9303 6.13641 85.1613 6.38904C85.3923 6.64166 85.6744 6.84222 85.9889 6.97735C86.3034 7.11249 86.6431 7.17911 86.9853 7.17278L86.8327 9.00398Z"
                  fill="#263238"
                />
                <path
                  d="M87.0702 61.6241H4.46184C4.4423 61.6247 4.42286 61.6211 4.40487 61.6134C4.38689 61.6058 4.37079 61.5943 4.35771 61.5797C4.34464 61.5652 4.33489 61.548 4.32915 61.5293C4.32342 61.5106 4.32183 61.4909 4.32451 61.4715L8.44165 14.2694C8.44542 14.2281 8.46434 14.1896 8.49477 14.1614C8.5252 14.1333 8.56499 14.1173 8.60646 14.1168H91.2118C91.2314 14.1162 91.251 14.1199 91.2691 14.1275C91.2872 14.1351 91.3034 14.1466 91.3167 14.1611C91.3301 14.1755 91.3401 14.1927 91.3462 14.2114C91.3523 14.23 91.3544 14.2498 91.3522 14.2694L87.235 61.4715C87.2313 61.5128 87.2123 61.5513 87.1819 61.5794C87.1515 61.6076 87.1117 61.6235 87.0702 61.6241ZM4.64191 61.3189H86.942L91.0348 14.422H8.73464L4.64191 61.3189Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M87.8828 52.1832H5.28663C5.26723 52.1833 5.24803 52.1793 5.23029 52.1715C5.21255 52.1636 5.19667 52.1521 5.18369 52.1377C5.17072 52.1233 5.16094 52.1063 5.15501 52.0878C5.14908 52.0694 5.14713 52.0499 5.14929 52.0306C5.15238 51.989 5.17109 51.9502 5.20167 51.9219C5.23225 51.8935 5.27242 51.8779 5.3141 51.878H87.9194C87.9391 51.8774 87.9586 51.8811 87.9767 51.8887C87.9948 51.8964 88.0111 51.9078 88.0244 51.9223C88.0377 51.9367 88.0477 51.9539 88.0539 51.9726C88.06 51.9913 88.062 52.0111 88.0598 52.0306C88.0545 52.0735 88.0335 52.1128 88.0007 52.141C87.968 52.1692 87.926 52.1843 87.8828 52.1832Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M88.7155 42.7442H6.11016C6.09065 42.7443 6.07134 42.7403 6.05346 42.7325C6.03558 42.7247 6.01952 42.7132 6.00631 42.6989C5.9931 42.6845 5.98303 42.6676 5.97674 42.6491C5.97045 42.6306 5.96807 42.6111 5.96977 42.5916C5.97425 42.5501 5.9938 42.5116 6.02473 42.4835C6.05566 42.4553 6.09583 42.4395 6.13763 42.439H88.743C88.7626 42.4385 88.7821 42.4421 88.8002 42.4498C88.8184 42.4574 88.8346 42.4689 88.8479 42.4833C88.8612 42.4978 88.8713 42.5149 88.8774 42.5336C88.8835 42.5523 88.8855 42.5721 88.8833 42.5916C88.8782 42.6329 88.8585 42.671 88.8277 42.699C88.7969 42.727 88.7571 42.7431 88.7155 42.7442Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M89.5395 33.3008H6.93422C6.91457 33.3014 6.89503 33.2977 6.87692 33.2901C6.8588 33.2824 6.84255 33.271 6.82924 33.2565C6.81593 33.242 6.80588 33.2249 6.79977 33.2062C6.79366 33.1875 6.79163 33.1677 6.79383 33.1482C6.79892 33.1069 6.81866 33.0688 6.84946 33.0408C6.88026 33.0128 6.92008 32.9968 6.96169 32.9956H89.567C89.5864 32.9955 89.6056 32.9995 89.6233 33.0073C89.6411 33.0151 89.657 33.0267 89.6699 33.0411C89.6829 33.0555 89.6927 33.0725 89.6986 33.091C89.7045 33.1094 89.7065 33.1289 89.7043 33.1482C89.7013 33.1898 89.6825 33.2286 89.652 33.2569C89.6214 33.2852 89.5812 33.3009 89.5395 33.3008Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M90.3637 23.8609H7.75844C7.73879 23.8615 7.71925 23.8578 7.70114 23.8502C7.68303 23.8425 7.66676 23.8311 7.65346 23.8166C7.64015 23.8022 7.6301 23.785 7.62399 23.7663C7.61788 23.7476 7.61585 23.7279 7.61804 23.7083C7.62181 23.667 7.64073 23.6286 7.67117 23.6004C7.7016 23.5722 7.74138 23.5563 7.78285 23.5557H90.3912C90.4108 23.5551 90.4302 23.5588 90.4482 23.5664C90.4662 23.5741 90.4823 23.5856 90.4953 23.6001C90.5084 23.6147 90.5182 23.6319 90.5239 23.6506C90.5296 23.6693 90.5312 23.689 90.5286 23.7083C90.5255 23.7499 90.5067 23.7887 90.4762 23.8171C90.4456 23.8454 90.4054 23.861 90.3637 23.8609Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M76.7426 61.6241C76.7231 61.6247 76.7036 61.6211 76.6856 61.6134C76.6677 61.6058 76.6516 61.5943 76.6385 61.5797C76.6254 61.5652 76.6157 61.548 76.6099 61.5293C76.6042 61.5106 76.6026 61.4909 76.6053 61.4715L80.7224 14.2694C80.7262 14.2281 80.7451 14.1896 80.7755 14.1614C80.806 14.1333 80.8457 14.1173 80.8872 14.1168C80.9069 14.1162 80.9264 14.1199 80.9445 14.1275C80.9626 14.1351 80.9789 14.1466 80.9922 14.1611C81.0055 14.1755 81.0156 14.1927 81.0217 14.2114C81.0278 14.23 81.0298 14.2498 81.0276 14.2694L76.9105 61.4715C76.906 61.5131 76.8864 61.5516 76.8555 61.5797C76.8246 61.6078 76.7844 61.6236 76.7426 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M66.4176 61.6241C66.398 61.6247 66.3784 61.621 66.3603 61.6134C66.3422 61.6057 66.3259 61.5943 66.3126 61.5798C66.2993 61.5654 66.2893 61.5482 66.2832 61.5295C66.2771 61.5108 66.275 61.4911 66.2772 61.4715L70.3944 14.2694C70.3988 14.2278 70.4184 14.1893 70.4493 14.1612C70.4803 14.1331 70.5204 14.1173 70.5622 14.1168C70.5818 14.1161 70.6012 14.1198 70.6192 14.1275C70.6372 14.1351 70.6533 14.1466 70.6664 14.1612C70.6794 14.1757 70.6892 14.1929 70.6949 14.2116C70.7007 14.2303 70.7022 14.25 70.6996 14.2694L66.5824 61.4715C66.5793 61.5131 66.5606 61.5519 66.53 61.5802C66.4995 61.6086 66.4593 61.6242 66.4176 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M56.0929 61.6241C56.0733 61.6247 56.0537 61.621 56.0356 61.6134C56.0175 61.6057 56.0012 61.5943 55.9879 61.5798C55.9746 61.5654 55.9646 61.5482 55.9585 61.5295C55.9524 61.5108 55.9503 61.4911 55.9525 61.4715L60.0697 14.2694C60.0741 14.2278 60.0937 14.1893 60.1246 14.1612C60.1555 14.1331 60.1957 14.1173 60.2375 14.1168C60.2571 14.1161 60.2765 14.1198 60.2945 14.1275C60.3125 14.1351 60.3286 14.1466 60.3417 14.1612C60.3547 14.1757 60.3645 14.1929 60.3702 14.2116C60.3759 14.2303 60.3775 14.25 60.3749 14.2694L56.2577 61.4715C56.254 61.5128 56.235 61.5513 56.2046 61.5794C56.1742 61.6076 56.1344 61.6235 56.0929 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M45.7648 61.6241C45.7451 61.6247 45.7256 61.621 45.7075 61.6134C45.6894 61.6057 45.6731 61.5943 45.6598 61.5798C45.6465 61.5654 45.6364 61.5482 45.6303 61.5295C45.6242 61.5108 45.6222 61.4911 45.6244 61.4715L49.7324 14.2694C49.7361 14.2281 49.7551 14.1896 49.7855 14.1614C49.8159 14.1333 49.8557 14.1173 49.8972 14.1168C49.9168 14.1162 49.9364 14.1199 49.9545 14.1275C49.9726 14.1351 49.9889 14.1466 50.0022 14.1611C50.0155 14.1755 50.0255 14.1927 50.0316 14.2114C50.0377 14.23 50.0398 14.2498 50.0376 14.2694L45.9326 61.4715C45.9288 61.5133 45.9095 61.5522 45.8784 61.5805C45.8473 61.6087 45.8068 61.6243 45.7648 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M35.4404 61.6241C35.4208 61.6247 35.4014 61.6211 35.3834 61.6134C35.3654 61.6058 35.3493 61.5943 35.3362 61.5797C35.3232 61.5652 35.3134 61.548 35.3077 61.5293C35.3019 61.5106 35.3003 61.4909 35.303 61.4715L39.4202 14.2694C39.4233 14.2278 39.442 14.1889 39.4725 14.1606C39.5031 14.1323 39.5433 14.1166 39.585 14.1168C39.6046 14.1162 39.6242 14.1199 39.6423 14.1275C39.6604 14.1351 39.6766 14.1466 39.69 14.1611C39.7033 14.1755 39.7133 14.1927 39.7194 14.2114C39.7255 14.23 39.7276 14.2498 39.7254 14.2694L35.6082 61.4715C35.6037 61.5131 35.5842 61.5516 35.5533 61.5797C35.5223 61.6078 35.4822 61.6236 35.4404 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M25.1149 61.6241C25.0952 61.6247 25.0757 61.621 25.0576 61.6134C25.0395 61.6057 25.0232 61.5943 25.0099 61.5798C24.9966 61.5654 24.9865 61.5482 24.9804 61.5295C24.9743 61.5108 24.9723 61.4911 24.9745 61.4715L29.0916 14.2694C29.0961 14.2278 29.1157 14.1893 29.1466 14.1612C29.1775 14.1331 29.2177 14.1173 29.2595 14.1168C29.279 14.1161 29.2985 14.1198 29.3165 14.1275C29.3344 14.1351 29.3505 14.1466 29.3636 14.1612C29.3767 14.1757 29.3864 14.1929 29.3922 14.2116C29.3979 14.2303 29.3995 14.25 29.3968 14.2694L25.2797 61.4715C25.2766 61.5131 25.2579 61.5519 25.2273 61.5802C25.1967 61.6086 25.1566 61.6242 25.1149 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M14.7902 61.6241C14.7705 61.6247 14.751 61.621 14.7329 61.6134C14.7148 61.6057 14.6985 61.5943 14.6852 61.5798C14.6719 61.5654 14.6618 61.5482 14.6557 61.5295C14.6496 61.5108 14.6476 61.4911 14.6498 61.4715L18.7669 14.2694C18.7714 14.2278 18.791 14.1893 18.8219 14.1612C18.8528 14.1331 18.893 14.1173 18.9348 14.1168C18.9543 14.1161 18.9738 14.1198 18.9918 14.1275C19.0097 14.1351 19.0258 14.1466 19.0389 14.1612C19.052 14.1757 19.0617 14.1929 19.0675 14.2116C19.0732 14.2303 19.0748 14.25 19.0721 14.2694L14.955 61.4715C14.9512 61.5128 14.9323 61.5513 14.9019 61.5794C14.8714 61.6076 14.8316 61.6235 14.7902 61.6241Z"
                  fill="#E0E0E0"
                />
                <path
                  d="M56.6518 32.2052H50.3433C50.2184 32.2073 50.0945 32.1827 49.9798 32.1332C49.8652 32.0836 49.7624 32.0102 49.6784 31.9177C49.5944 31.8253 49.531 31.7161 49.4925 31.5972C49.454 31.4784 49.4413 31.3527 49.4552 31.2286L49.9435 25.6282C49.9696 25.3639 50.0919 25.1184 50.2872 24.9384C50.4824 24.7583 50.737 24.6562 51.0025 24.6515H57.311C57.4361 24.649 57.5604 24.6733 57.6754 24.7227C57.7904 24.772 57.8936 24.8454 57.978 24.9379C58.0623 25.0303 58.126 25.1398 58.1646 25.2588C58.2033 25.3779 58.2161 25.5038 58.2022 25.6282L57.7139 31.2286C57.6877 31.4934 57.5649 31.7393 57.369 31.9194C57.1732 32.0995 56.9178 32.2013 56.6518 32.2052Z"
                  fill="#2AB9B8"
                />
                <path
                  opacity="0.6"
                  d="M56.6518 32.2052H50.3433C50.2184 32.2073 50.0945 32.1827 49.9798 32.1332C49.8652 32.0836 49.7624 32.0102 49.6784 31.9177C49.5944 31.8253 49.531 31.7161 49.4925 31.5972C49.454 31.4784 49.4413 31.3527 49.4552 31.2286L49.9435 25.6282C49.9696 25.3639 50.0919 25.1184 50.2872 24.9384C50.4824 24.7583 50.737 24.6562 51.0025 24.6515H57.311C57.4361 24.649 57.5604 24.6733 57.6754 24.7227C57.7904 24.772 57.8936 24.8454 57.978 24.9379C58.0623 25.0303 58.126 25.1398 58.1646 25.2588C58.2033 25.3779 58.2161 25.5038 58.2022 25.6282L57.7139 31.2286C57.6877 31.4934 57.5649 31.7393 57.369 31.9194C57.1732 32.0995 56.9178 32.2013 56.6518 32.2052Z"
                  fill="white"
                />
                <path
                  d="M100.667 32.2051H94.0352L94.6913 24.6514H101.326L98.8329 28.4297L100.667 32.2051Z"
                  fill="white"
                />
                <path
                  d="M100.914 32.3579H93.8672L94.5539 24.499H101.616L99.0251 28.43L100.914 32.3579ZM94.1999 32.0527H100.417L98.6558 28.43L101.049 24.8042H94.8316L94.1999 32.0527Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M76.4191 41.6455H70.1107C69.9858 41.6479 69.8618 41.6238 69.7471 41.5746C69.6323 41.5254 69.5293 41.4523 69.445 41.3602C69.3606 41.268 69.2969 41.159 69.258 41.0403C69.2192 40.9216 69.206 40.796 69.2195 40.6719L69.7078 35.0684C69.7354 34.8044 69.8586 34.5596 70.0543 34.3803C70.2499 34.2009 70.5045 34.0994 70.7699 34.0948H77.0784C77.2031 34.0929 77.3268 34.1174 77.4414 34.1667C77.5559 34.2161 77.6587 34.2892 77.7429 34.3812C77.8271 34.4732 77.8909 34.582 77.93 34.7005C77.969 34.819 77.9825 34.9444 77.9695 35.0684L77.4812 40.6719C77.4537 40.9359 77.3304 41.1807 77.1348 41.36C76.9391 41.5394 76.6845 41.6409 76.4191 41.6455Z"
                  fill="#2AB9B8"
                />
                <path
                  opacity="0.4"
                  d="M76.4191 41.6455H70.1107C69.9858 41.6479 69.8618 41.6238 69.7471 41.5746C69.6323 41.5254 69.5293 41.4523 69.445 41.3602C69.3606 41.268 69.2969 41.159 69.258 41.0403C69.2192 40.9216 69.206 40.796 69.2195 40.6719L69.7078 35.0684C69.7354 34.8044 69.8586 34.5596 70.0543 34.3803C70.2499 34.2009 70.5045 34.0994 70.7699 34.0948H77.0784C77.2031 34.0929 77.3268 34.1174 77.4414 34.1667C77.5559 34.2161 77.6587 34.2892 77.7429 34.3812C77.8271 34.4732 77.8909 34.582 77.93 34.7005C77.969 34.819 77.9825 34.9444 77.9695 35.0684L77.4812 40.6719C77.4537 40.9359 77.3304 41.1807 77.1348 41.36C76.9391 41.5394 76.6845 41.6409 76.4191 41.6455Z"
                  fill="white"
                />
                <path
                  d="M88.5083 22.7647H71.759C71.6338 22.7673 71.5096 22.743 71.3945 22.6936C71.2795 22.6443 71.1764 22.5709 71.092 22.4784C71.0076 22.386 70.944 22.2765 70.9053 22.1575C70.8666 22.0384 70.8538 21.9125 70.8678 21.7881L71.3561 16.1877C71.3822 15.9234 71.5045 15.6779 71.6998 15.4979C71.895 15.3178 72.1496 15.2157 72.4151 15.2111H89.1675C89.2925 15.209 89.4165 15.2336 89.5313 15.2832C89.6461 15.3327 89.7491 15.4061 89.8333 15.4984C89.9176 15.5908 89.9813 15.7 90.0201 15.8188C90.059 15.9377 90.0721 16.0634 90.0587 16.1877L89.5704 21.7881C89.5442 22.0529 89.4214 22.2988 89.2256 22.4789C89.0297 22.6591 88.7744 22.7608 88.5083 22.7647Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M21.6906 30.9297C19.1452 30.9297 17.0241 30.0294 15.5195 28.305C13.5876 26.0862 13.2976 23.3333 14.7595 21.1176C16.9295 17.8367 22.5849 16.0879 26.3052 16.5731C26.3247 16.5756 26.3434 16.5822 26.3602 16.5923C26.377 16.6025 26.3915 16.6161 26.4027 16.6322C26.4139 16.6483 26.4216 16.6666 26.4253 16.6858C26.429 16.7051 26.4287 16.7249 26.4243 16.7441C26.4148 16.7844 26.3911 16.82 26.3574 16.8441C26.3237 16.8683 26.2824 16.8793 26.2411 16.8753C22.6123 16.4022 17.1126 18.0961 15.0128 21.2702C13.3587 23.7728 14.3139 26.4341 15.7606 28.0944C17.778 30.4078 20.5278 30.7344 22.475 30.5971C26.1954 30.3376 30.2698 28.2104 32.6168 25.3049C33.2729 24.4931 34.2801 22.9335 33.6636 21.6425C32.748 19.7106 29.696 19.8449 27.1506 20.1745C27.1315 20.177 27.112 20.1756 27.0934 20.1703C27.0748 20.165 27.0575 20.1558 27.0426 20.1435C27.0277 20.1312 27.0155 20.1159 27.0068 20.0987C26.9981 20.0814 26.993 20.0626 26.9919 20.0433C26.9908 20.0017 27.0052 19.9612 27.0323 19.9296C27.0594 19.898 27.0972 19.8776 27.1384 19.8723C28.8414 19.6496 32.8273 19.1338 33.9413 21.496C34.6311 22.9488 33.5507 24.6274 32.8487 25.5002C30.4468 28.4729 26.2747 30.652 22.4719 30.9145C22.2095 30.9206 21.9592 30.9297 21.6906 30.9297Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M72.6221 39.9637H72.5977C72.5499 39.9604 72.5039 39.9446 72.4642 39.9178C72.4246 39.891 72.3927 39.8542 72.3718 39.8111C72.0574 39.0915 71.6252 38.4293 71.093 37.8517C71.0331 37.7922 70.9993 37.7114 70.999 37.627C70.9987 37.5426 71.032 37.4615 71.0915 37.4016C71.151 37.3417 71.2318 37.3079 71.3163 37.3076C71.4007 37.3073 71.4818 37.3405 71.5417 37.4C72.031 37.9116 72.4359 38.4978 72.7411 39.1366C73.7898 38.0596 74.926 37.0715 76.138 36.1823C76.201 36.1333 76.2807 36.111 76.3601 36.1202C76.4394 36.1293 76.5119 36.1691 76.5622 36.2311C76.5855 36.2648 76.6018 36.3028 76.6102 36.3428C76.6185 36.3829 76.6187 36.4243 76.6107 36.4644C76.6027 36.5046 76.5868 36.5427 76.5637 36.5766C76.5407 36.6104 76.5111 36.6393 76.4767 36.6615C75.1765 37.6121 73.9678 38.6818 72.8662 39.8569C72.8351 39.8906 72.7973 39.9174 72.7553 39.9358C72.7133 39.9542 72.6679 39.9637 72.6221 39.9637Z"
                  fill="white"
                />
                <path
                  d="M61.3821 60.1764C60.9433 60.1837 60.508 60.0976 60.105 59.9239C59.7021 59.7501 59.3406 59.4926 59.0448 59.1684C58.7489 58.8443 58.5254 58.4609 58.389 58.0438C58.2527 57.6267 58.2066 57.1853 58.2538 56.749C58.3492 55.8215 58.7809 54.9609 59.4675 54.33C60.1541 53.6991 61.048 53.3414 61.9803 53.3247C62.4189 53.3169 62.8542 53.4026 63.2572 53.5761C63.6602 53.7495 64.0216 54.0067 64.3175 54.3306C64.6134 54.6545 64.837 55.0377 64.9734 55.4547C65.1098 55.8717 65.1559 56.3129 65.1086 56.749C65.0133 57.6769 64.5817 58.5378 63.8952 59.1692C63.2086 59.8007 62.3147 60.1589 61.3821 60.1764ZM61.9406 53.9351C61.1764 53.9519 60.4447 54.247 59.8827 54.7652C59.3207 55.2833 58.9673 55.9887 58.8886 56.749C58.8496 57.1076 58.8873 57.4703 58.9993 57.8131C59.1112 58.1559 59.2948 58.471 59.5378 58.7375C59.7809 59.0039 60.0778 59.2156 60.4089 59.3584C60.7401 59.5013 61.0978 59.572 61.4584 59.566C62.2229 59.5485 62.9547 59.2527 63.5166 58.7341C64.0785 58.2155 64.4318 57.5097 64.5104 56.749C64.5485 56.3909 64.51 56.0287 64.3977 55.6864C64.2854 55.3442 64.1017 55.0297 63.8588 54.7637C63.6159 54.4977 63.3193 54.2863 62.9887 54.1435C62.658 54.0007 62.3008 53.9296 61.9406 53.9351Z"
                  fill="#2AB9B8"
                />
                <path
                  d="M32.031 41.2605C31.5923 41.2678 31.1569 41.1817 30.754 41.0079C30.351 40.8341 29.9896 40.5766 29.6937 40.2525C29.3979 39.9283 29.1744 39.545 29.038 39.1279C28.9016 38.7108 28.8555 38.2694 28.9027 37.8331C28.9983 36.9032 29.4317 36.0406 30.1207 35.4089C30.8098 34.7772 31.7067 34.4202 32.6414 34.4057C33.0802 34.3984 33.5156 34.4845 33.9185 34.6583C34.3215 34.8321 34.6829 35.0896 34.9788 35.4137C35.2746 35.7378 35.4981 36.1212 35.6345 36.5383C35.7709 36.9554 35.817 37.3968 35.7697 37.8331C35.6736 38.7628 35.24 39.625 34.551 40.2565C33.8621 40.8881 32.9656 41.2453 32.031 41.2605ZM32.5743 35.0161C31.8096 35.033 31.0775 35.3285 30.5154 35.8473C29.9534 36.366 29.6002 37.0722 29.5223 37.8331C29.4833 38.1916 29.521 38.5543 29.6329 38.8972C29.7448 39.24 29.9284 39.5551 30.1715 39.8215C30.4145 40.0879 30.7115 40.2996 31.0426 40.4425C31.3738 40.5853 31.7315 40.6561 32.0921 40.6501C32.8565 40.6326 33.5883 40.3368 34.1502 39.8181C34.7121 39.2995 35.0655 38.5937 35.1441 37.8331C35.1825 37.4752 35.1446 37.1132 35.0329 36.771C34.9211 36.4288 34.738 36.1143 34.4958 35.848C34.2535 35.5818 33.9575 35.37 33.6273 35.2266C33.2972 35.0832 32.9403 35.0115 32.5804 35.0161H32.5743Z"
                  fill="#2AB9B8"
                />
              </svg>
            </div>
            <div className="info-text-wrapper">
              <h3 className="info-title">
                {t("Filling prediction for containers")}
              </h3>
              <p className="info-description">
                {t("Filling prediction description")}
              </p>
              {hasInsufficientHistory && (
                <div style={{ marginTop: "16px" }}>
                  <p
                    className="info-description"
                    style={{
                      marginBottom: "8px",
                      color: "#F59E0B",
                      fontWeight: "500",
                    }}
                  >
                    {t("Prediction history warning")}
                  </p>
                  {containersWithInsufficientHistory.length > 0 && (
                    <div>
                      <button
                        onClick={() =>
                          setShowInsufficientHistoryList(
                            !showInsufficientHistoryList,
                          )
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px 0",
                          color: "#F59E0B",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            transform: showInsufficientHistoryList
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          <path
                            d="M4.5 3L7.5 6L4.5 9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {showInsufficientHistoryList
                          ? t("Hide containers with insufficient history")
                          : t("View containers with insufficient history")}
                      </button>
                      {showInsufficientHistoryList && (
                        <ul
                          style={{
                            marginTop: "8px",
                            marginLeft: "20px",
                            paddingLeft: "0",
                            listStyle: "none",
                          }}
                        >
                          {containersWithInsufficientHistory.map(
                            (container) => (
                              <li
                                key={container.id}
                                style={{
                                  fontSize: "13px",
                                  color: "#5E5E5E",
                                  marginBottom: "4px",
                                  lineHeight: "1.5",
                                }}
                              >
                                • {container.name} — {container.site}
                                {container.positioning &&
                                  ` — ${container.positioning.charAt(0).toUpperCase() + container.positioning.slice(1)}`}
                              </li>
                            ),
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="fc-card-wrapper">
        <div className="fc-calendar-header">
          <div className="fc-month-title">
            {monthWeekInfo.months.length === 0
              ? moment()
                  .locale(i18n.language || "fr")
                  .format("MMMM YYYY")
              : monthWeekInfo.months.length === 1
                ? monthWeekInfo.months[0]
                : `${monthWeekInfo.months[0]} - ${monthWeekInfo.months[monthWeekInfo.months.length - 1]}`}
          </div>
          <div className="fc-week-title">
            {monthWeekInfo.weeks.length === 0
              ? `${t("Week")} ${moment()
                  .locale(i18n.language || "fr")
                  .week()}`
              : monthWeekInfo.weeks.length === 1
                ? `${t("Week")} ${monthWeekInfo.weeks[0]}`
                : `${t("Week")} ${monthWeekInfo.weeks[0]} - ${monthWeekInfo.weeks[monthWeekInfo.weeks.length - 1]}`}
          </div>
        </div>

        <div className="fc-calendar-container">
          <div className="fc-time-column">
            {hours.map((hour) => {
              const hourString = String(hour).padStart(2, "0");
              const separator = (i18n.language || "fr").startsWith("fr")
                ? "h"
                : ":";
              return (
                <div key={hour} className="fc-time-slot">
                  {hourString}
                  {separator}00
                </div>
              );
            })}
          </div>

          <div className="fc-days-container">
            {days.map((day, dayIdx) => {
              const locale = i18n.language || "fr";
              const dayMoment = moment(day).tz(timezone).locale(locale);
              const isTodayDay = isToday(day);

              return (
                <div
                  key={dayIdx}
                  className={`fc-day-column ${isTodayDay ? "fc-day-today" : ""}`}
                >
                  <div className="fc-day-header">
                    <div className="fc-day-weekday">
                      {dayMoment.format("ddd").toUpperCase()}
                    </div>
                    <div className="fc-day-number">{dayMoment.format("D")}</div>
                  </div>

                  <div className="fc-day-hours">
                    {hours.map((hour, hourIdx) => {
                      const totalHours = 20 - 6 + 1; // 15 hours (6 to 20 inclusive)
                      const positionPercent = ((hour - 6) / totalHours) * 100;
                      return (
                        <div
                          key={`hour-${hourIdx}`}
                          className="fc-hour-line"
                          style={{ top: `${positionPercent}%` }}
                        ></div>
                      );
                    })}
                    <div className="fc-hour-line" style={{ top: "100%" }}></div>
                    {getEventsForDay(dayMoment).map((event) => {
                      const eventStart = moment(event.start).tz(timezone);
                      const eventEnd = moment(event.end).tz(timezone);
                      const predictionMoment =
                        event.extendedProps?.predictionMoment;
                      const predictionDatetime =
                        event.extendedProps?.predictionDatetime;
                      const containerTimezone =
                        event.extendedProps?.containerTimezone || timezone;

                      // Get the prediction time as center
                      let centerMoment = null;
                      if (
                        predictionMoment &&
                        predictionMoment.isValid() &&
                        predictionMoment.isSame(dayMoment, "day")
                      ) {
                        centerMoment = predictionMoment.clone().tz(timezone);
                      } else if (predictionDatetime) {
                        const predMoment =
                          moment(predictionDatetime).tz(containerTimezone);
                        if (
                          predMoment.isValid() &&
                          predMoment.isSame(dayMoment, "day")
                        ) {
                          centerMoment = predMoment.clone().tz(timezone);
                        }
                      }

                      // If no valid center, use midpoint of event
                      if (!centerMoment || !centerMoment.isValid()) {
                        centerMoment = eventStart
                          .clone()
                          .add(eventEnd.diff(eventStart) / 2);
                      }

                      // Calculate position relative to day start (6:00)
                      const dayStart = dayMoment
                        .clone()
                        .hour(6)
                        .minute(0)
                        .second(0)
                        .millisecond(0);
                      const dayEnd = dayMoment
                        .clone()
                        .hour(20)
                        .minute(0)
                        .second(0)
                        .millisecond(0);
                      const dayDurationMs =
                        dayEnd.valueOf() - dayStart.valueOf();

                      // Always use 1 hour block duration (60 minutes)
                      const blockDurationMs = 60 * 60 * 1000;
                      const halfDuration = blockDurationMs / 2;

                      // Center the event on prediction time
                      const centerMs = centerMoment.valueOf();
                      let actualStartMs = centerMs - halfDuration;
                      let actualEndMs = centerMs + halfDuration;

                      // Ensure event is within day bounds (6:00 - 20:00)
                      const originalStartMs = actualStartMs;
                      const originalEndMs = actualEndMs;
                      actualStartMs = Math.max(
                        actualStartMs,
                        dayStart.valueOf(),
                      );
                      actualEndMs = Math.min(actualEndMs, dayEnd.valueOf());

                      // If the block was clipped, try to maintain 1 hour by adjusting the center
                      const clippedAtStart = actualStartMs > originalStartMs;
                      const clippedAtEnd = actualEndMs < originalEndMs;

                      if (clippedAtStart || clippedAtEnd) {
                        // Try to maintain 1 hour duration by shifting the center
                        if (clippedAtStart && !clippedAtEnd) {
                          // Clipped at start, shift center forward
                          actualStartMs = dayStart.valueOf();
                          actualEndMs = Math.min(
                            dayStart.valueOf() + blockDurationMs,
                            dayEnd.valueOf(),
                          );
                        } else if (clippedAtEnd && !clippedAtStart) {
                          // Clipped at end, shift center backward
                          actualEndMs = dayEnd.valueOf();
                          actualStartMs = Math.max(
                            dayEnd.valueOf() - blockDurationMs,
                            dayStart.valueOf(),
                          );
                        } else {
                          // Clipped on both sides, use available space centered on prediction
                          const availableSpace = actualEndMs - actualStartMs;
                          if (availableSpace >= blockDurationMs) {
                            // Enough space, center the 1-hour block on prediction
                            actualStartMs = centerMs - halfDuration;
                            actualEndMs = centerMs + halfDuration;
                            actualStartMs = Math.max(
                              actualStartMs,
                              dayStart.valueOf(),
                            );
                            actualEndMs = Math.min(
                              actualEndMs,
                              dayEnd.valueOf(),
                            );
                          }
                          // If not enough space, keep the clipped version (will be less than 1 hour)
                        }
                      }

                      // Ensure minimum height (20 minutes)
                      const minHeightMs = 20 * 60 * 1000;
                      if (actualEndMs - actualStartMs < minHeightMs) {
                        const availableBefore = centerMs - dayStart.valueOf();
                        const availableAfter = dayEnd.valueOf() - centerMs;
                        const neededHalf = minHeightMs / 2;

                        if (
                          availableBefore >= neededHalf &&
                          availableAfter >= neededHalf
                        ) {
                          actualStartMs = centerMs - neededHalf;
                          actualEndMs = centerMs + neededHalf;
                        } else if (availableBefore < neededHalf) {
                          actualStartMs = dayStart.valueOf();
                          actualEndMs = Math.min(
                            dayStart.valueOf() + minHeightMs,
                            dayEnd.valueOf(),
                          );
                        } else {
                          actualEndMs = dayEnd.valueOf();
                          actualStartMs = Math.max(
                            dayEnd.valueOf() - minHeightMs,
                            dayStart.valueOf(),
                          );
                        }
                      }

                      const startPercent =
                        ((actualStartMs - dayStart.valueOf()) / dayDurationMs) *
                        100;
                      const endPercent =
                        ((actualEndMs - dayStart.valueOf()) / dayDurationMs) *
                        100;
                      const heightPercent = Math.max(
                        endPercent - startPercent,
                        1.5,
                      ); // Minimum 1.5% height

                      let timeDisplay = "";
                      if (predictionDatetime) {
                        const formatted = formattedPredictionDateTime(
                          predictionDatetime,
                          containerTimezone,
                          t,
                        );
                        if (formatted) {
                          timeDisplay = formatted;
                        }
                      }

                      const tooltip = `${event.title}\n${event.extendedProps.address}${event.extendedProps.site ? `\n${event.extendedProps.site}` : ""}${event.extendedProps.positioning ? `\n${event.extendedProps.positioning}` : ""}${timeDisplay ? `\n${timeDisplay}` : ""}`;

                      return (
                        <div
                          key={event.id}
                          className="fc-event-block"
                          style={{
                            top: `${startPercent}%`,
                            height: `${heightPercent}%`,
                            backgroundColor: event.backgroundColor,
                            borderColor: event.borderColor,
                            color: event.textColor,
                          }}
                          title={tooltip}
                        >
                          {timeDisplay && (
                            <div className="fc-ev-time">{timeDisplay}</div>
                          )}
                          <div className="fc-ev-title">{event.title}</div>
                          <div className="fc-ev-addr">
                            {event.extendedProps.address}
                          </div>
                          {event.extendedProps.positioning && (
                            <div className="fc-ev-positioning">
                              {event.extendedProps.positioning
                                .charAt(0)
                                .toUpperCase() +
                                event.extendedProps.positioning.slice(1)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .fc-body-flat {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .urgent-banner {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 6px 35px;
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
          width: 100%;
          min-height: 100px;
          background: #FDF7EC;
          border: 1px solid #EEAC43;
          border-radius: 8px;
          gap: 8px;
        }

        .urgent-title-wrapper {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 8px;
          width: 100%;
        }

        .urgent-icon {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .urgent-title {
          font-size: 14px;
          font-weight: 600;
          color: #222222;
          line-height: 1.4;
        }

        .urgent-subtitle {
          font-size: 14px;
          font-weight: 400;
          color: #222222;
          margin-top: 4px;
        }

        .urgent-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
          margin-top: 4px;
        }

        .urgent-item {
          display: flex;
          align-items: center;
        }

        .urgent-item-text {
          font-size: 14px;
          color: #222222;
          line-height: 1.4;
        }

        .urgent-item-text::before {
          content: "• ";
          margin-right: 4px;
        }

        .fc-calendar-header {
          text-align: center;
          margin-bottom: 20px;
          margin-top: 8px;
        }

        .fc-month-title {
          font-size: 20px;
          font-weight: 700;
          color: #222222;
          margin-bottom: 8px;
          text-transform: capitalize;
        }

        .fc-week-title {
          font-size: 14px;
          font-weight: 400;
          color: #5E5E5E;
        }

        .fc-card-wrapper {
          background: #fff;
          border: 1.5px solid #e0e0e0;
          border-radius: 8px;
          padding: 35px;
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
          margin-top: 8px;
          overflow: hidden;
          width: 100%;
        }

        .fc-calendar-container {
          display: flex;
          width: 100%;
        }

        .fc-time-column {
          width: 60px;
          flex-shrink: 0;
          padding-top: 64px;
        }

        .fc-time-slot {
          height: 60px;
          display: flex;
          align-items: flex-start;
          padding: 4px 8px;
          font-size: 11px;
          color: #666;
        }

        .fc-days-container {
          display: flex;
          flex: 1;
        }

        .fc-day-column {
          flex: 1;
          border-right: 1px solid #e0e0e0;
          display: flex;
          flex-direction: column;
        }

        .fc-day-column:last-child {
          border-right: none;
        }

        .fc-day-today {
          background: #F6F6F6;
        }

        .fc-day-header {
          height: 64px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          border-bottom: 1.5px solid #e0e0e0;
          padding: 8px 12px;
        }

        .fc-day-weekday {
          font-size: 13px;
          color: #222222;
          font-weight: 400;
          text-transform: uppercase;
        }

        .fc-day-number {
          font-size: 18px;
          color: #222222;
          font-weight: 400;
          margin-top: 4px;
        }

        .fc-day-hours {
          flex: 1;
          position: relative;
          min-height: ${(20 - 6 + 1) * 60}px;
        }

        .fc-hour-line {
          position: absolute;
          left: 0;
          right: 0;
          height: 1.5px;
          background: #e0e0e0;
          z-index: 0;
        }

        .fc-event-block {
          position: absolute;
          left: 2px;
          right: 2px;
          border-radius: 4px;
          padding: 4px;
          font-size: 11px;
          overflow-y: visible;
          overflow-x: hidden;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-height: 30px;
          z-index: 1;
        }

        .fc-event-block:hover {
          z-index: 2;
        }

        .fc-ev-time {
          font-size: 9px;
          font-weight: 600;
          opacity: 0.8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fc-ev-title {
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fc-ev-addr {
          font-size: 10px;
          font-weight: 600;
          opacity: 0.85;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fc-ev-positioning {
          font-size: 9px;
          font-weight: 500;
          opacity: 0.7;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fc-ev-action {
          font-size: 9px;
          font-weight: 700;
          opacity: 1;
          white-space: normal;
          overflow: hidden;
          word-wrap: break-word;
          margin-top: 3px;
          line-height: 1.3;
          padding: 2px 4px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .info-banner {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 24px 35px;
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
          width: 100%;
          max-width: 100%;
          background: #fff;
          border: 1.5px solid #e0e0e0;
          border-radius: 8px;
          position: relative;
          margin-top: 0.6rem;
          overflow: hidden;
        }

        .info-close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.6;
          transition: opacity 0.2s;
        }

        .info-close-button:hover {
          opacity: 1;
        }

        .info-content {
          display: flex;
          flex-direction: row;
          gap: 24px;
          width: 100%;
          align-items: stretch;
          box-sizing: border-box;
          min-width: 0;
        }

        .info-svg-wrapper {
          flex-shrink: 0;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 150px;
          min-width: 150px;
          padding: 20px 0;
        }

        .info-svg-wrapper svg {
          height: auto;
          width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .info-text-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 0;
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
          box-sizing: border-box;
          justify-content: center;
        }

        .info-title {
          font-size: 18px;
          font-weight: 700;
          color: #222222;
          margin: 0;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          max-width: 100%;
          box-sizing: border-box;
        }

        .info-description {
          font-size: 14px;
          font-weight: 400;
          color: #5E5E5E;
          line-height: 1.6;
          margin: 0;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          max-width: 100%;
          box-sizing: border-box;
          width: 100%;
          white-space: normal;
          hyphens: auto;
        }
      `}</style>
    </div>
  );
};

export default FillingCalendar;
